import axios from "axios"
import type { ProjectCrew } from "../hooks/types"
import type {
  Project,
  ProjectMeta,
  ProjectFormValues,
  CostCodeBudget,
  ProjectImportAnalysis,
  ProjectImportPreview,
  ProjectImportConfirmation,
  ProjectImportResult,
} from "../hooks/types"

// Projects API service
export const projectsApi = {
  // List all projects
  getProjects: async (): Promise<Project[]> => {
    const response = await axios.get<{ data: Project[] }>("/api/myteconstruction/projects")
    return response.data.data
  },

  // Get a single project by ID
  getProject: async (id: string): Promise<Project> => {
    const response = await axios.get<{ data: Project }>(`/api/myteconstruction/projects/${id}`)
    return response.data.data
  },

  // Create a new project
  createProject: async (project: ProjectFormValues): Promise<Project> => {
    const response = await axios.post<{ data: Project }>("/api/myteconstruction/projects", project)
    return response.data.data
  },

  // Update an existing project
  updateProject: async (id: string, project: Partial<ProjectFormValues>): Promise<Project> => {
    const response = await axios.put<{ data: Project }>(`/api/myteconstruction/projects/${id}`, project)
    return response.data.data
  },

  // Delete a project
  deleteProject: async (id: string): Promise<void> => {
    await axios.delete(`/api/myteconstruction/projects/${id}`)
  },

  // Get project metadata (clients, personnel, cost codes, etc.)
  getProjectMeta: async (): Promise<ProjectMeta> => {
    const response = await axios.get<{ data: ProjectMeta }>("/api/myteconstruction/projects/meta")
    return response.data.data
  },

  // Update cost code budgets for a project
  updateCostCodeBudgets: async (id: string, costCodeBudgets: CostCodeBudget[]): Promise<Project> => {
    const response = await axios.put<{ data: Project }>(`/api/myteconstruction/projects/${id}`, { costCodeBudgets })
    return response.data.data
  },

  // Remove a seat from a project
  removeSeatFromProject: async (projectId: string, seatId: string): Promise<Project> => {
    const response = await axios.post<{ data: Project }>(
      `/api/myteconstruction/projects/${projectId}/seats/${seatId}/remove`,
      {},
    )
    return response.data.data
  },

  // Attach a seat to a project
  attachSeatToProject: async (projectId: string, seatId: string, personnelId: string): Promise<Project> => {
    const response = await axios.post<{ data: Project }>(`/api/myteconstruction/projects/${projectId}/attach`, {
      seatId,
      personnelId,
    })
    return response.data.data
  },

  // Excel import flow
  import: {
    // Download template
    getTemplate: async (): Promise<Blob> => {
      const response = await axios.get("/api/myteconstruction/projects/import/template", {
        responseType: "blob",
      })
      return response.data
    },

    // Analyze Excel file
    analyzeExcel: async (file: File): Promise<ProjectImportAnalysis> => {
      const formData = new FormData()
      formData.append("excelFile", file)
      const response = await axios.post<ProjectImportAnalysis>(
        "/api/myteconstruction/projects/import/analyze",
        formData,
      )
      return response.data
    },

    // Preview Excel import
    previewImport: async (file: File): Promise<ProjectImportPreview> => {
      const formData = new FormData()
      formData.append("excelFile", file)
      const response = await axios.post<ProjectImportPreview>("/api/myteconstruction/projects/import", formData)
      return response.data
    },

    // Confirm import
    confirmImport: async (data: ProjectImportConfirmation): Promise<ProjectImportResult> => {
      const response = await axios.post<ProjectImportResult>("/api/myteconstruction/projects/import/confirm", data)
      return response.data
    },
  },  // <-- close import block
  // --- Crew Management endpoints ---
  // --- Crew Management endpoints ---
  // Fetch crew assignments for a project
  getCrew: async (projectId: string): Promise<ProjectCrew[]> => {
    const response = await axios.get<{ crews: ProjectCrew[] }>(
      `/api/myteconstruction/projects/${projectId}/crew`
    )
    return response.data.crews
  },
  // Upsert crew assignments (bulk optional)
  upsertCrew: async (
    projectId: string,
    crews: ProjectCrew[],
    bulk = false
  ): Promise<ProjectCrew[]> => {
    const response = await axios.post<ProjectCrew[]>(
      `/api/myteconstruction/projects/${projectId}/crew`,
      { crews, bulk }
    )
    return response.data
  },
  // Reactivate an inactive ironworker in a crew
  reactivateWorker: async (
    projectId: string,
    foremanId: string,
    workerId: string
  ): Promise<void> => {
    await axios.patch(
      `/api/myteconstruction/projects/${projectId}/crew/${foremanId}/reactivate/${workerId}`
    )
  },
  // Set or update an ironworker's nickname for a foreman
  setNickname: async (
    projectId: string,
    foremanId: string,
    workerId: string,
    nickname: string
  ): Promise<{ workerId: string; foremanId: string; nickname: string }> => {
    const response = await axios.patch<{
        workerId: string
        foremanId: string
        nickname: string
      }>(
      `/api/myteconstruction/projects/${projectId}/crew/${foremanId}/nickname/${workerId}`,
      { nickname }
    )
    return response.data
  },
}
